import { SF852013, SF85 } from 'constants/formTypes';
import { formTypes } from 'config/formTypes';
import { usStatesValues } from 'constants/enums/usStates';
import { usTerritoriesPVQValues, usTerritoriesSFValues } from 'constants/enums/usTerritories';

/**
 * @param {string} formType
 * @param {SectionConfig} sectionConfigPartialFilter - This partial can match
 * some of the pieces within the sectionConfig object. The filter is pretty
 * basic at the moment and allows filtering on first-level properties.
 * @returns {SectionConfig}
 */

export const getSectionFromFormType = (formType, sectionConfigPartialFilter) => ({
  key: null,
  name: null,
  path: null,
  label: null,
  store: null,
  subsections: [],
  ...formTypes[formType].find(
    (section) => ![
      ...Object.keys(sectionConfigPartialFilter).map(
        (key) => section[key] === sectionConfigPartialFilter[key]
      ),
    ].includes(false)
  ),
});

/**
 *
 * @param {SubsectionConfig[]} subsections
 * @param {string} name
 * @returns {?SubsectionConfig}
 */
const findSubsection = (subsections, name) => {
  for (const subsection of subsections) {
    if (subsection.name === name) { return subsection; }
    if (subsection.subsections) {
      let output = null;
      output = findSubsection(subsection.subsections, name);

      if (output !== null) { return output; }
    }
  }

  return null;
};

/**
 * @param {string} formType
 * @param {string} sectionName
 * @param {string} subsectionName
 * @returns {SubsectionConfig}
 */
export const getSubsectionFromFormType = (formType, sectionName, subsectionName) => ({
  key: null,
  label: null,
  name: null,
  path: null,
  ...findSubsection(
    getSectionFromFormType(formType, { name: sectionName }).subsections,
    subsectionName
  ),
});

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flat
function flatDeep(arr, d = 1) {
  return d > 0
    ? arr.reduce((acc, val) => acc.concat(Array.isArray(val) ? flatDeep(val, d - 1) : val), [])
    : arr.slice();
}

/**
 * @param {string} formType
 * @param {string} sectionKey
 * @returns {string[]}
 */
export const getChildSubsectionKeys = (formType, sectionKey) => flatDeep(
  getSectionFromFormType(formType, { key: sectionKey }).subsections.map((subsection) => {
    let keys = [subsection.key];

    if (subsection.subsections) {
      keys = [...keys, ...subsection.subsections.map((e) => e.key)];
    }

    return keys;
  })
);

/**
 * Returns True if formtype is SF852013 or SF85 with version 2013-12
 * @param {string} formType
 * @param {string} formVersion
 * @returns {Boolean}
 */
export function isFormSF852013(formType, formVersion) {
  return (formType === SF852013 || (formType === SF85 && formVersion === '2013-12') || false);
}

/**
 * Returns True if formtype is SF852013 or SF85 with version 2013-12
 * @param {string} formType
 * @param {string} formVersion
 * @returns {string}
 */
export function getQualifiedFormType(formType, formVersion) {
  if (formType === SF85) {
    return (isFormSF852013(formType, formVersion) ? SF852013 : formType);
  }
  return formType;
}

/**
 * Check formtype for the presence of PVQ
 * @param {*} formType
 * @returns boolean
 */
export const isPVQForm = (formType: string | undefined) => !!formType?.includes?.('PVQ');

export const formIncludesPvqPartB = (formType: string | undefined) => isPVQForm(formType) && Boolean(formType?.includes?.('B'));
export const formIncludesPvqPartC = (formType: string | undefined) => isPVQForm(formType) && Boolean(formType?.includes?.('C'));

// Credit Freeze should not display for PVQ or anything beyond 2024-02; so new version of SF* will need to be added here.
export const shouldDisplayCreditFreeze = (formType, formVersion) => !isPVQForm(formType) && !['2024-02'].includes(formVersion);

// Returns inclusions of US states and territories based on form type
export const getUSInclusionsByForm = (formType: string | undefined): string[] => {
  if (isPVQForm(formType)) {
    return [...usStatesValues, ...usTerritoriesPVQValues]
  }

  return [...usStatesValues, ...usTerritoriesSFValues]
}
